import { create } from 'zustand';
import type { Room, Participant, PlaybackState } from '@/types';

interface RoomStore {
  room: Room | null;
  participantId: string | null;
  participantName: string | null;
  role: 'host' | 'guest' | null;

  // Actions
  setRoom: (room: Room) => void;
  updateRoom: (partial: Partial<Room>) => void;
  setParticipant: (id: string, name: string, role: 'host' | 'guest') => void;
  addParticipant: (p: Participant) => void;
  removeParticipant: (id: string) => void;
  transferHost: (newHostId: string) => void;
  updatePlayback: (state: PlaybackState, time: number, version: number) => void;
  clearRoom: () => void;
}

export const useRoomStore = create<RoomStore>((set) => ({
  room: null,
  participantId: null,
  participantName: null,
  role: null,

  setRoom: (room) => set({ room }),

  updateRoom: (partial) =>
    set((s) => (s.room ? { room: { ...s.room, ...partial } } : {})),

  setParticipant: (id, name, role) =>
    set({ participantId: id, participantName: name, role }),

  addParticipant: (p) =>
    set((s) =>
      s.room
        ? {
            room: {
              ...s.room,
              participants: [
                ...s.room.participants.filter((x) => x.id !== p.id),
                p,
              ],
            },
          }
        : {}
    ),

  removeParticipant: (id) =>
    set((s) =>
      s.room
        ? {
            room: {
              ...s.room,
              participants: s.room.participants.filter((p) => p.id !== id),
            },
          }
        : {}
    ),

  transferHost: (newHostId) =>
    set((s) => {
      if (!s.room) return {};
      const newParticipants = s.room.participants.map((p) => ({
        ...p,
        role: (p.id === newHostId ? 'host' : 'guest') as 'host' | 'guest',
      }));
      const myNewRole = s.participantId === newHostId ? 'host' : 'guest';
      return {
        role: myNewRole,
        room: {
          ...s.room,
          hostId: newHostId,
          participants: newParticipants,
        },
      };
    }),

  updatePlayback: (playbackState, currentTime, version) =>
    set((s) =>
      s.room && version >= s.room.version
        ? { room: { ...s.room, playbackState, currentTime, version } }
        : {}
    ),

  clearRoom: () =>
    set({ room: null, participantId: null, participantName: null, role: null }),
}));
